import { useParams } from 'react-router-dom'

import { useAnalysisHistoryDetailApi } from '@/common/api/modules/mediaAnalysis'
import {
  priorityBadgeType,
  priorityLabel,
  volumeBadgeType
} from '@/common/app/modules/media/articleCreator'
import { PriorityLevel } from '@/common/model/media/articleCreator'
import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Line from '@/components/view/layouts/area/Line'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import PriorityBar from '@/components/view/molecules/bar/PriorityBar'

import AnalysisActionList from '@/components/container/modules/media/action/AnalysisActionList'

import MediaAnalysisResultPageBase from './MediaAnalysisResultPageBase'

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 }

const COVERAGE_ITEMS: {
  key: 'well_covered' | 'partially_covered' | 'not_covered'
  label: string
  color: TextColor
  bgColor: BgColor
}[] = [
  {
    key: 'well_covered',
    label: '十分にカバー済み',
    color: TextColor.SUCCESS,
    bgColor: BgColor.SECONDARY
  },
  {
    key: 'partially_covered',
    label: '部分的にカバー',
    color: TextColor.CAUTION,
    bgColor: BgColor.SECONDARY
  },
  {
    key: 'not_covered',
    label: '全くカバーされていない',
    color: TextColor.CRITICAL,
    bgColor: BgColor.CAUTION
  }
]

interface ContentGapItem {
  area: string
  description?: string
  priority?: PriorityLevel
  estimated_search_volume?: PriorityLevel
  suggested_article_title?: string
  potential_keywords?: string[]
  related_categories?: string[]
  related_brands?: string[]
}
interface ContentGapQuickWin {
  action: string
  expected_impact?: string
}
interface ContentGapResultData {
  coverage_analysis?: {
    well_covered?: string[]
    partially_covered?: string[]
    not_covered?: string[]
  }
  gaps?: ContentGapItem[]
  quick_wins?: ContentGapQuickWin[]
  // AI応答のJSON解析に失敗した場合、result_dataがこの形になる（backend/routers/article_creator.py::_parse_json_response）
  error?: string
  raw?: string
}

// コンテンツギャップ分析結果
const MediaAnalysisGapResultPage = () => {
  const { siteId, historyId } = useParams()
  const sid = Number(siteId)
  const hid = Number(historyId)
  // 分析中（status=analyzing）の間は2秒間隔でポーリングし、完了したら自動的に停止する
  const { data, error } = useAnalysisHistoryDetailApi(
    siteId && historyId ? { site_id: sid, history_id: hid } : undefined,
    { refreshInterval: (latest) => (latest?.status === 'analyzing' ? 2000 : 0) }
  )

  const result = (data?.result_data ?? {}) as ContentGapResultData
  const sortedGaps = [...(result.gaps ?? [])].sort(
    (a, b) =>
      (PRIORITY_ORDER[a.priority ?? 'low'] ?? 9) - (PRIORITY_ORDER[b.priority ?? 'low'] ?? 9)
  )

  return (
    <MediaAnalysisResultPageBase
      type={MediaAnalysisType.CONTENT_GAP}
      data={data}
      error={error}
      resultError={result.error}
    >
      {result.coverage_analysis && (
        <>
          <SummaryHeading>カバレッジ分析</SummaryHeading>
          <Area bottomMargin={16}>
            <SummaryItemList shadow>
              {COVERAGE_ITEMS.map((item) => {
                const items = result.coverage_analysis?.[item.key] ?? []
                return (
                  <SummaryItem key={item.key}>
                    <Text size={12} weight='bold'>
                      {item.label}
                    </Text>
                    <ItemAlign gapX={4} align='baseline'>
                      <Text size={24} weight='bold' color={item.color}>
                        {items.length}
                      </Text>
                      <Text size={14}>件</Text>
                    </ItemAlign>
                    {items.length > 0 && (
                      <div className='flex flex-wrap justify-center gap-1'>
                        {items.map((s, i) => (
                          <Badge key={i} size={10} badgeType={BadgeType.SQUARE_SECONDARY}>
                            {s}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </SummaryItem>
                )
              })}
            </SummaryItemList>
          </Area>
        </>
      )}

      <AnalysisActionList siteId={sid} mediaAnalysisId={hid} />

      {sortedGaps.length > 0 && (
        <Area bottomMargin={16}>
          <Heading>コンテンツギャップ ({sortedGaps.length}件)</Heading>
          <Line />
          <AreaContent>
            <div className='flex flex-col gap-3.5'>
              {sortedGaps.map((gap, i) => (
                <div key={i} className='flex gap-2'>
                  <div className='w-[50%]'>
                    <PriorityBar
                      priority={gap.priority ?? 'low'}
                      badgeText={`検索ボリューム: ${priorityLabel(gap.estimated_search_volume)}`}
                      title={gap.area}
                      contentText={gap.description}
                    />
                  </div>

                  {gap.suggested_article_title && (
                    <>
                      <div className='flex items-center justify-center'>
                        <Icon type={IconType.MOVE_RIGHT} />
                      </div>
                      <div className='w-[50%]'>
                        <AiInsideArea>
                          <div className='flex flex-col gap-2 p-2'>
                            <Text size={12} color={TextColor.AI} weight='bold'>
                              記事追加
                            </Text>
                            <div className='flex flex-col gap-2 px-2'>
                              <div className='flex flex-col gap-1'>
                                <Badge badgeType={BadgeType.SQUARE_AI} size={12}>
                                  推奨記事タイトル案
                                </Badge>
                                <Text weight='bold' size={16}>
                                  {gap.suggested_article_title}
                                </Text>
                              </div>
                              {gap.potential_keywords && gap.potential_keywords.length > 0 && (
                                <div className='flex gap-1'>
                                  <Badge badgeType={BadgeType.SQUARE_SECONDARY} size={12} fixed>
                                    想定キーワード
                                  </Badge>
                                  <div className='flex flex-wrap gap-1.5'>
                                    {gap.potential_keywords.map((kw, ki) => (
                                      <Badge
                                        key={ki}
                                        size={10}
                                        badgeType={BadgeType.KEYWORD_ON_BG_FILL}
                                      >
                                        {kw}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {gap.related_categories && gap.related_categories.length > 0 && (
                                <div className='flex gap-1'>
                                  <Badge badgeType={BadgeType.SQUARE_SECONDARY} size={12} fixed>
                                    関連カテゴリ
                                  </Badge>
                                  <div className='flex flex-wrap gap-1'>
                                    {gap.related_categories.map((cat, ci) => (
                                      <Badge key={ci} size={12} badgeType={BadgeType.KEYWORD}>
                                        {cat}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {gap.related_brands && gap.related_brands.length > 0 && (
                                <div className='flex gap-1'>
                                  <Badge badgeType={BadgeType.SQUARE_SECONDARY} size={12} fixed>
                                    関連ブランド
                                  </Badge>
                                  <div className='flex flex-wrap gap-1'>
                                    {gap.related_brands.map((br, bi) => (
                                      <Badge
                                        key={bi}
                                        size={12}
                                        badgeType={BadgeType.SQUARE_PRIMARY}
                                      >
                                        {br}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </AiInsideArea>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </AreaContent>
        </Area>
      )}
      {/* クイックウィンは不要として、一旦非表示、削除予定 */}
      {/* {result.quick_wins && result.quick_wins.length > 0 && (
      <Area bottomMargin={16}>
        <Heading>クイックウィン（すぐにできるアクション）</Heading>
        <Line />
        <AreaContent>
          <div className='flex flex-col gap-2.5'>
            {result.quick_wins.map((qw, i) => (
              <InsideArea key={i} color={BgColor.SECONDARY} border>
                <div className='flex items-start gap-3.5'>
                  <div className='bg-fill-success-secondary flex h-7 w-7 shrink-0 items-center justify-center rounded-full'>
                    <Text size={12} weight='bold' color={TextColor.SUCCESS}>
                      {i + 1}
                    </Text>
                  </div>
                  <div className='flex flex-1 flex-col gap-1'>
                    <Text weight='bold'>{qw.action}</Text>
                    {qw.expected_impact && (
                      <Text size={12} color={TextColor.SECONDARY}>
                        期待効果: {qw.expected_impact}
                      </Text>
                    )}
                  </div>
                </div>
              </InsideArea>
            ))}
          </div>
        </AreaContent>
      </Area>
    )} */}
    </MediaAnalysisResultPageBase>
  )
}

export default MediaAnalysisGapResultPage
